import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Truck, ShieldCheck, ReceiptText } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.status === "2fa-enroll") {
        navigate("/2fa/setup", { replace: true });
      } else if (result.status === "2fa-verify") {
        navigate("/2fa/verify", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] bg-slate-950" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex relative overflow-hidden order-1 text-white">
        {/* Layered backgrounds */}
        <div className="absolute inset-0 bg-slate-950" />
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            background:
              "radial-gradient(60% 50% at 80% 0%, rgba(249,115,22,0.55), transparent 60%), radial-gradient(40% 35% at 10% 100%, rgba(255,255,255,0.18), transparent 60%)",
          }}
        />
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage:
              "radial-gradient(60% 70% at 50% 40%, black 50%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(60% 70% at 50% 40%, black 50%, transparent 100%)",
          }}
        />

        <div className="relative z-10 flex flex-col w-full p-12 xl:p-16">
          {/* Top brand strip */}
          <div className="flex items-center gap-4">
            <img
              src="/d4ps-logo.png"
              alt="D4PS"
              className="h-14 w-14 rounded-sm object-contain bg-black/40 p-1.5"
              data-testid="login-hero-logo"
            />
            <div>
              <div className="font-display text-xl font-semibold tracking-tight leading-none">D4PS</div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-slate-400 mt-1">Couriers &amp; Logistics</div>
            </div>
            <div className="ml-auto text-[10px] uppercase tracking-[0.28em] text-orange-400/95 font-semibold">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-orange-500 mr-2 align-middle" />
              Operations portal
            </div>
          </div>

          {/* Hero */}
          <div className="mt-auto pt-24 max-w-xl">
            <div className="h-[3px] w-12 bg-orange-500 mb-6" />
            <h2 className="font-display text-4xl xl:text-5xl font-semibold tracking-tight text-white leading-[1.05]">
              The control room for every job, vehicle and pound.
            </h2>
            <p className="mt-5 text-slate-300/90 text-[15px] leading-relaxed max-w-md">
              One secure portal for the whole D4PS operation — from raising a PO at 6am to closing the books on Friday afternoon.
            </p>

            {/* Feature pills */}
            <div className="mt-10 grid grid-cols-3 gap-3 max-w-lg">
              <Feature icon={Truck}        label="Operations" sub="Jobs · Sites · Drivers" />
              <Feature icon={ReceiptText}  label="Finance"    sub="POs · Self-bills · Reports" />
              <Feature icon={ShieldCheck}  label="Compliance" sub="Fleet · WTNs · Documents" />
            </div>
          </div>

          {/* Footer */}
          <div className="mt-auto pt-16 flex items-center justify-between text-xs text-slate-500">
            <span>&copy; {new Date().getFullYear()} D4PS Couriers &amp; Logistics Ltd</span>
            <span className="font-mono tracking-widest">Internal staff portal</span>
          </div>
        </div>
      </div>

      {/* Right sign-in form */}
      <div className="relative flex items-center justify-center px-6 py-12 bg-white order-2">
        {/* Top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600" />
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex justify-center">
            <Logo variant="dark" size="lg" />
          </div>

          <div className="text-xs uppercase tracking-[0.22em] text-orange-600 font-semibold">Welcome back</div>
          <h1 className="mt-3 font-display text-3xl md:text-4xl tracking-tight font-semibold text-slate-900">
            Sign in to your portal
          </h1>
          <div className="mt-4 h-px w-12 bg-slate-200" />

          <form className="mt-8 space-y-5" onSubmit={submit}>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-slate-500" htmlFor="email">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-sm h-11 border-slate-300 focus-visible:ring-slate-900"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.18em] text-slate-500" htmlFor="password">
                  Password
                </Label>
                <span className="text-[11px] text-slate-400">Contact admin to reset</span>
              </div>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-sm h-11 border-slate-300 focus-visible:ring-slate-900"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm"
              >
                {error}
              </div>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-sm bg-slate-900 hover:bg-slate-800 text-white font-medium relative overflow-hidden group"
            >
              <span className="absolute inset-y-0 left-0 w-1 bg-orange-500 group-hover:w-1.5 transition-all" />
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Sign in
            </Button>
          </form>

          <div className="mt-12 pt-6 border-t border-slate-100 text-[11px] text-slate-500">
            <span>D4PS Couriers &amp; Logistics Ltd</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, label, sub }) {
  return (
    <div className="rounded-sm border border-white/10 bg-white/[0.03] px-3.5 py-3 backdrop-blur-sm">
      <Icon className="h-4 w-4 text-orange-500" />
      <div className="mt-2 text-[13px] font-medium text-white">{label}</div>
      <div className="mt-0.5 text-[10.5px] text-slate-400 leading-tight">{sub}</div>
    </div>
  );
}
